import { useState, useMemo } from 'react'
import { useDepartment } from '../../context/DepartmentContext'
import {
  forecastDemand, detectAnomalies, getPricingSignals,
  getProductionPlan, getContributionMarginReport
} from '../../lib/intelligence'
import {
  Brain, TrendingUp, TrendingDown, AlertTriangle, Target, Zap,
  BarChart3, DollarSign, Package, ArrowUp, ArrowDown, Activity
} from 'lucide-react'

const TABS = [
  { id: 'forecast', label: 'Demand Forecast', icon: TrendingUp },
  { id: 'anomalies', label: 'Anomaly Detection', icon: AlertTriangle },
  { id: 'pricing', label: 'Pricing Signals', icon: DollarSign },
  { id: 'margin', label: 'Contribution Margin', icon: BarChart3 },
]

function fmt(n) { return `R${(n || 0).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` }
function fmtR(n) { return `R${(n || 0).toLocaleString('en-ZA', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}` }
function fmtPct(n) { return `${(n || 0).toFixed(1)}%` }

function StatCard({ label, value, icon: Icon, sub, color = 'brand' }) {
  const colors = {
    brand: 'text-brand-400',
    green: 'text-green-400',
    red: 'text-red-400',
    amber: 'text-amber-400',
    blue: 'text-blue-400',
  }
  return (
    <div className="bg-gray-900 border border-gray-800 p-5">
      <div className="flex items-center justify-between mb-2">
        <span className="text-[11px] font-semibold tracking-widest uppercase text-gray-400">{label}</span>
        {Icon && <Icon size={16} className={colors[color] || colors.brand} />}
      </div>
      <div className="font-display text-2xl text-white tracking-wide">{value}</div>
      {sub && <div className="text-xs text-gray-500 mt-1">{sub}</div>}
    </div>
  )
}

function Badge({ children, variant = 'default' }) {
  const styles = {
    default: 'bg-gray-800 text-gray-300 border-gray-700',
    good: 'bg-green-900/40 text-green-400 border-green-800',
    fair: 'bg-amber-900/40 text-amber-400 border-amber-800',
    sparse: 'bg-red-900/40 text-red-400 border-red-800',
    high: 'bg-green-900/40 text-green-400 border-green-800',
    medium: 'bg-amber-900/40 text-amber-400 border-amber-800',
    low: 'bg-red-900/40 text-red-400 border-red-800',
    critical: 'bg-red-900/40 text-red-400 border-red-800',
    info: 'bg-blue-900/40 text-blue-400 border-blue-800',
  }
  return (
    <span className={`text-[11px] font-semibold px-2 py-0.5 border ${styles[variant] || styles.default}`}>
      {children}
    </span>
  )
}

// ─────────────────────────────────────────
// Tab: Demand Forecast
// ─────────────────────────────────────────

function DemandForecastTab({ departmentId }) {
  const tomorrow = new Date()
  tomorrow.setDate(tomorrow.getDate() + 1)
  const [targetDate, setTargetDate] = useState(tomorrow.toISOString().split('T')[0])

  const data = useMemo(() => {
    try { return forecastDemand(departmentId, targetDate) }
    catch { return null }
  }, [departmentId, targetDate])

  const production = useMemo(() => {
    try { return getProductionPlan(departmentId, targetDate) }
    catch { return null }
  }, [departmentId, targetDate])

  if (!data) return <div className="text-gray-400 text-center py-16">Unable to generate forecast.</div>

  return (
    <div className="space-y-6">
      {/* Controls */}
      <div className="flex flex-wrap items-center gap-4">
        <label className="text-[11px] font-semibold tracking-widest uppercase text-gray-400">Forecast Date</label>
        <input
          type="date"
          value={targetDate}
          onChange={e => setTargetDate(e.target.value)}
          className="bg-gray-900 border border-gray-700 px-3 py-2 text-sm text-white focus:outline-none focus:border-brand-500"
        />
        <Badge variant={data.dataQuality}>{data.dataQuality} data</Badge>
        {data.isPayday && <Badge variant="high">Payday Period</Badge>}
        <span className="text-sm text-gray-400">{data.dayOfWeek}</span>
      </div>

      {/* Summary stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <StatCard label="Forecast Revenue" value={fmtR(data.totalForecastRevenue)} icon={TrendingUp} color="green" />
        <StatCard label="Forecast COGS" value={fmtR(data.totalForecastCOGS)} icon={Package} color="red" />
        <StatCard
          label="Forecast Profit"
          value={fmtR(data.forecastProfit)}
          icon={data.forecastProfit >= 0 ? ArrowUp : ArrowDown}
          color={data.forecastProfit >= 0 ? 'green' : 'red'}
          sub={data.totalForecastRevenue > 0 ? `${((data.forecastProfit / data.totalForecastRevenue) * 100).toFixed(1)}% margin` : ''}
        />
      </div>

      {/* Product forecast table */}
      {data.products.length > 0 ? (
        <div className="bg-gray-900 border border-gray-800 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-800 text-[11px] font-semibold tracking-widest uppercase text-gray-400">
                <th className="text-left px-4 py-3">Product</th>
                <th className="text-left px-4 py-3">Code</th>
                <th className="text-right px-4 py-3">Forecast Qty</th>
                <th className="text-right px-4 py-3">Avg Daily</th>
                <th className="text-right px-4 py-3">Revenue</th>
                <th className="text-center px-4 py-3">Confidence</th>
                <th className="text-left px-4 py-3">Method</th>
              </tr>
            </thead>
            <tbody>
              {data.products.map(p => (
                <tr key={p.productId} className="border-b border-gray-800/50 hover:bg-gray-800/30">
                  <td className="px-4 py-3 text-white font-medium">{p.productName}</td>
                  <td className="px-4 py-3 text-gray-400 font-mono text-xs">{p.code}</td>
                  <td className="px-4 py-3 text-right text-white">{p.forecastQty}</td>
                  <td className="px-4 py-3 text-right text-gray-400">{p.avgDailyQty}</td>
                  <td className="px-4 py-3 text-right text-green-400">{fmt(p.forecastRevenue)}</td>
                  <td className="px-4 py-3 text-center">
                    <Badge variant={p.confidence}>{p.confidence}</Badge>
                  </td>
                  <td className="px-4 py-3 text-gray-500 text-xs">{p.method.replace(/_/g, ' ')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="bg-gray-900 border border-gray-800 p-12 text-center text-gray-500">
          No products to forecast for this date.
        </div>
      )}

      {/* Production Plan */}
      {production && production.items.length > 0 && (
        <div className="bg-gray-900 border border-gray-800">
          <div className="px-4 py-3 border-b border-gray-800">
            <h3 className="text-[11px] font-semibold tracking-widest uppercase text-gray-400">Production Plan</h3>
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-800 text-[11px] font-semibold tracking-widest uppercase text-gray-400">
                <th className="text-left px-4 py-3">Product</th>
                <th className="text-left px-4 py-3">Code</th>
                <th className="text-right px-4 py-3">Forecast Qty</th>
                <th className="text-right px-4 py-3">Revenue</th>
                <th className="text-center px-4 py-3">Confidence</th>
                <th className="text-center px-4 py-3">Priority</th>
              </tr>
            </thead>
            <tbody>
              {production.items.map(item => (
                <tr key={item.productId} className="border-b border-gray-800/50 hover:bg-gray-800/30">
                  <td className="px-4 py-3 text-white font-medium">{item.productName}</td>
                  <td className="px-4 py-3 text-gray-400 font-mono text-xs">{item.code}</td>
                  <td className="px-4 py-3 text-right text-white">{item.forecastQty}</td>
                  <td className="px-4 py-3 text-right text-green-400">{fmt(item.forecastRevenue)}</td>
                  <td className="px-4 py-3 text-center"><Badge variant={item.confidence}>{item.confidence}</Badge></td>
                  <td className="px-4 py-3 text-center">
                    <Badge variant={item.priority === 'high' ? 'high' : item.priority === 'medium' ? 'fair' : 'default'}>
                      {item.priority}
                    </Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Low Stock Alerts */}
      {production && production.lowStockAlerts.length > 0 && (
        <div className="bg-gray-900 border border-red-900/50">
          <div className="px-4 py-3 border-b border-red-900/50 flex items-center gap-2">
            <AlertTriangle size={16} className="text-red-400" />
            <h3 className="text-[11px] font-semibold tracking-widest uppercase text-red-400">
              Low Stock Alerts ({production.lowStockAlerts.length})
            </h3>
          </div>
          <div className="divide-y divide-gray-800/50">
            {production.lowStockAlerts.map(alert => (
              <div key={alert.itemId} className={`px-4 py-3 flex items-center justify-between ${alert.urgent ? 'bg-red-950/20' : ''}`}>
                <div>
                  <span className={`font-medium text-sm ${alert.urgent ? 'text-red-400' : 'text-white'}`}>{alert.name}</span>
                  {alert.urgent && <Badge variant="critical">OUT OF STOCK</Badge>}
                </div>
                <div className="text-sm text-gray-400">
                  {alert.currentStock} / {alert.reorderLevel} {alert.unit}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

// ─────────────────────────────────────────
// Tab: Anomaly Detection
// ─────────────────────────────────────────

function AnomalyDetectionTab({ departmentId }) {
  const today = new Date().toISOString().split('T')[0]
  const [date, setDate] = useState(today)

  const data = useMemo(() => {
    try { return detectAnomalies(departmentId, date) }
    catch { return null }
  }, [departmentId, date])

  if (!data) return <div className="text-gray-400 text-center py-16">Unable to run anomaly detection.</div>

  if (data.message) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <label className="text-[11px] font-semibold tracking-widest uppercase text-gray-400">Date</label>
          <input
            type="date"
            value={date}
            onChange={e => setDate(e.target.value)}
            className="bg-gray-900 border border-gray-700 px-3 py-2 text-sm text-white focus:outline-none focus:border-brand-500"
          />
        </div>
        <div className="bg-gray-900 border border-gray-800 p-12 text-center text-gray-500">
          <AlertTriangle size={32} className="mx-auto mb-3 text-amber-500" />
          {data.message}
        </div>
      </div>
    )
  }

  const zAbs = Math.abs(data.todayZScore)
  const zColor = zAbs > 3 ? 'text-red-400' : zAbs > 2 ? 'text-amber-400' : 'text-green-400'
  const zBarWidth = Math.min(100, (zAbs / 4) * 100)

  const severityStyles = {
    CRITICAL: 'bg-red-900/40 text-red-400 border-red-800',
    HIGH: 'bg-amber-900/40 text-amber-400 border-amber-800',
    MEDIUM: 'bg-blue-900/40 text-blue-400 border-blue-800',
  }

  return (
    <div className="space-y-6">
      {/* Controls */}
      <div className="flex items-center gap-4">
        <label className="text-[11px] font-semibold tracking-widest uppercase text-gray-400">Date</label>
        <input
          type="date"
          value={date}
          onChange={e => setDate(e.target.value)}
          className="bg-gray-900 border border-gray-700 px-3 py-2 text-sm text-white focus:outline-none focus:border-brand-500"
        />
      </div>

      {/* Revenue overview */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <StatCard
          label="Today's Revenue"
          value={fmtR(data.todayRevenue)}
          icon={DollarSign}
          color={data.todayZScore > 0 ? 'green' : data.todayZScore < -1 ? 'red' : 'brand'}
        />
        <StatCard
          label="Baseline (Mean)"
          value={fmtR(data.baseline.mean)}
          icon={Activity}
          sub={`± ${fmtR(data.baseline.stdDev)} std dev`}
          color="blue"
        />
        <div className="bg-gray-900 border border-gray-800 p-5">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[11px] font-semibold tracking-widest uppercase text-gray-400">Z-Score</span>
            <Target size={16} className={zColor} />
          </div>
          <div className={`font-display text-2xl tracking-wide ${zColor}`}>
            {data.todayZScore > 0 ? '+' : ''}{data.todayZScore.toFixed(2)}
          </div>
          {/* Visual gauge */}
          <div className="mt-3 h-2 bg-gray-800 relative">
            <div
              className={`h-full transition-all ${zAbs > 3 ? 'bg-red-500' : zAbs > 2 ? 'bg-amber-500' : 'bg-green-500'}`}
              style={{ width: `${zBarWidth}%` }}
            />
            <div className="absolute top-3 left-1/2 w-px h-2 bg-gray-600" />
            <div className="flex justify-between text-[10px] text-gray-600 mt-1">
              <span>0</span>
              <span>±2</span>
              <span>±4</span>
            </div>
          </div>
        </div>
      </div>

      {/* Anomalies list */}
      {data.anomalies.length > 0 ? (
        <div className="space-y-3">
          <h3 className="text-[11px] font-semibold tracking-widest uppercase text-gray-400">Detected Anomalies</h3>
          {data.anomalies.map((a, i) => (
            <div key={i} className="bg-gray-900 border border-gray-800 p-4 flex items-start gap-4">
              <div className="mt-0.5">
                {a.type.includes('SPIKE') ? (
                  <ArrowUp size={20} className="text-amber-400" />
                ) : a.type.includes('DROP') ? (
                  <ArrowDown size={20} className="text-red-400" />
                ) : (
                  <AlertTriangle size={20} className="text-amber-400" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-white font-medium text-sm">{a.type.replace(/_/g, ' ')}</span>
                  <span className={`text-[11px] font-semibold px-2 py-0.5 border ${severityStyles[a.severity] || severityStyles.MEDIUM}`}>
                    {a.severity}
                  </span>
                </div>
                <p className="text-sm text-gray-400">{a.message}</p>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-gray-900 border border-gray-800 p-12 text-center">
          <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center bg-green-900/30 border border-green-800">
            <Zap size={24} className="text-green-400" />
          </div>
          <p className="text-green-400 font-medium mb-1">No anomalies detected</p>
          <p className="text-sm text-gray-500">All metrics are within normal range for {date}.</p>
        </div>
      )}

      {/* Baseline stats */}
      <div className="bg-gray-900 border border-gray-800 p-5">
        <h3 className="text-[11px] font-semibold tracking-widest uppercase text-gray-400 mb-3">Baseline Statistics</h3>
        <div className="grid grid-cols-3 gap-4 text-sm">
          <div>
            <span className="text-gray-500 block">Mean Revenue</span>
            <span className="text-white font-medium">{fmtR(data.baseline.mean)}</span>
          </div>
          <div>
            <span className="text-gray-500 block">Std Deviation</span>
            <span className="text-white font-medium">{fmtR(data.baseline.stdDev)}</span>
          </div>
          <div>
            <span className="text-gray-500 block">Days of Data</span>
            <span className="text-white font-medium">{data.baseline.days} days</span>
          </div>
        </div>
      </div>
    </div>
  )
}

// ─────────────────────────────────────────
// Tab: Pricing Signals
// ─────────────────────────────────────────

function PricingSignalsTab({ departmentId }) {
  const signals = useMemo(() => {
    try { return getPricingSignals(departmentId) }
    catch { return [] }
  }, [departmentId])

  const summary = useMemo(() => {
    const lossMakers = signals.filter(s => s.signal === 'LOSS_MAKER').length
    const underpriced = signals.filter(s => s.signal === 'UNDERPRICED').length
    const highMargin = signals.filter(s => s.signal === 'HIGH_MARGIN').length
    return { lossMakers, underpriced, highMargin }
  }, [signals])

  const signalStyles = {
    LOSS_MAKER: { bg: 'border-red-800 bg-red-900/20', accent: 'text-red-400', icon: TrendingDown },
    UNDERPRICED: { bg: 'border-amber-800 bg-amber-900/20', accent: 'text-amber-400', icon: AlertTriangle },
    HIGH_MARGIN: { bg: 'border-blue-800 bg-blue-900/20', accent: 'text-blue-400', icon: TrendingUp },
  }

  return (
    <div className="space-y-6">
      {/* Summary */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <StatCard label="Loss Makers" value={summary.lossMakers} icon={TrendingDown} color="red" />
        <StatCard label="Underpriced" value={summary.underpriced} icon={AlertTriangle} color="amber" />
        <StatCard label="High Margin" value={summary.highMargin} icon={TrendingUp} color="blue" />
      </div>

      {/* Signal cards */}
      {signals.length > 0 ? (
        <div className="space-y-3">
          {signals.map((s, i) => {
            const style = signalStyles[s.signal] || signalStyles.UNDERPRICED
            const SignalIcon = style.icon
            return (
              <div key={i} className={`bg-gray-900 border p-5 ${style.bg}`}>
                <div className="flex items-start gap-4">
                  <SignalIcon size={20} className={style.accent} />
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2 mb-2">
                      <span className="text-white font-medium">{s.productName}</span>
                      <span className="text-gray-500 font-mono text-xs">{s.code}</span>
                      <Badge variant={s.severity === 'CRITICAL' ? 'critical' : s.severity === 'HIGH' ? 'medium' : 'info'}>
                        {s.signal.replace(/_/g, ' ')}
                      </Badge>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 text-sm mb-2">
                      <div>
                        <span className="text-gray-500 block text-xs">Current Price</span>
                        <span className="text-white">{fmt(s.currentPrice)}</span>
                      </div>
                      <div>
                        <span className="text-gray-500 block text-xs">COGS</span>
                        <span className="text-white">{fmt(s.cogs)}</span>
                      </div>
                      <div>
                        <span className="text-gray-500 block text-xs">Current Margin</span>
                        <span className={s.currentMargin < 0 ? 'text-red-400' : s.currentMargin < 15 ? 'text-amber-400' : 'text-green-400'}>
                          {fmtPct(s.currentMargin)}
                        </span>
                      </div>
                      {s.suggestedPrice && (
                        <div>
                          <span className="text-gray-500 block text-xs">Suggested Price</span>
                          <span className="text-brand-400 font-medium">{fmt(s.suggestedPrice)}</span>
                        </div>
                      )}
                      {s.suggestedMargin != null && (
                        <div>
                          <span className="text-gray-500 block text-xs">Suggested Margin</span>
                          <span className="text-green-400 font-medium">{fmtPct(s.suggestedMargin)}</span>
                        </div>
                      )}
                    </div>
                    <p className="text-sm text-gray-400">{s.message}</p>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      ) : (
        <div className="bg-gray-900 border border-gray-800 p-12 text-center">
          <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center bg-green-900/30 border border-green-800">
            <Zap size={24} className="text-green-400" />
          </div>
          <p className="text-green-400 font-medium mb-1">All products healthy</p>
          <p className="text-sm text-gray-500">No pricing anomalies detected. All margins are within acceptable range.</p>
        </div>
      )}
    </div>
  )
}

// ─────────────────────────────────────────
// Tab: Contribution Margin
// ─────────────────────────────────────────

function ContributionMarginTab({ departmentId }) {
  const today = new Date().toISOString().split('T')[0]
  const thirtyDaysAgo = new Date(Date.now() - 30 * 86400000).toISOString().split('T')[0]
  const [dateFrom, setDateFrom] = useState(thirtyDaysAgo)
  const [dateTo, setDateTo] = useState(today)

  const data = useMemo(() => {
    try { return getContributionMarginReport(departmentId, dateFrom, dateTo) }
    catch { return null }
  }, [departmentId, dateFrom, dateTo])

  if (!data) return <div className="text-gray-400 text-center py-16">Unable to generate margin report.</div>

  return (
    <div className="space-y-6">
      {/* Date range controls */}
      <div className="flex flex-wrap items-center gap-4">
        <label className="text-[11px] font-semibold tracking-widest uppercase text-gray-400">From</label>
        <input
          type="date"
          value={dateFrom}
          onChange={e => setDateFrom(e.target.value)}
          className="bg-gray-900 border border-gray-700 px-3 py-2 text-sm text-white focus:outline-none focus:border-brand-500"
        />
        <label className="text-[11px] font-semibold tracking-widest uppercase text-gray-400">To</label>
        <input
          type="date"
          value={dateTo}
          onChange={e => setDateTo(e.target.value)}
          className="bg-gray-900 border border-gray-700 px-3 py-2 text-sm text-white focus:outline-none focus:border-brand-500"
        />
      </div>

      {/* Totals */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <StatCard label="Revenue" value={fmtR(data.totals.revenue)} icon={DollarSign} color="brand" />
        <StatCard label="COGS" value={fmtR(data.totals.cogs)} icon={Package} color="red" />
        <StatCard
          label="Contribution Margin"
          value={fmtR(data.totals.contributionMargin)}
          icon={data.totals.contributionMargin >= 0 ? ArrowUp : ArrowDown}
          color={data.totals.contributionMargin >= 0 ? 'green' : 'red'}
        />
        <StatCard
          label="CM %"
          value={fmtPct(data.totals.contributionPct)}
          icon={Target}
          color={data.totals.contributionPct >= 30 ? 'green' : data.totals.contributionPct >= 15 ? 'amber' : 'red'}
        />
      </div>

      {/* Top 5 contributors */}
      {data.topContributors.length > 0 && (
        <div className="bg-gray-900 border border-gray-800">
          <div className="px-4 py-3 border-b border-gray-800">
            <h3 className="text-[11px] font-semibold tracking-widest uppercase text-green-400">Top 5 Contributors</h3>
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-800 text-[11px] font-semibold tracking-widest uppercase text-gray-400">
                <th className="text-left px-4 py-3">#</th>
                <th className="text-left px-4 py-3">Product</th>
                <th className="text-right px-4 py-3">Revenue</th>
                <th className="text-right px-4 py-3">COGS</th>
                <th className="text-right px-4 py-3">CM</th>
                <th className="text-right px-4 py-3">CM %</th>
              </tr>
            </thead>
            <tbody>
              {data.topContributors.map((p, i) => (
                <tr key={p.productId} className="border-b border-gray-800/50 hover:bg-gray-800/30">
                  <td className="px-4 py-3 text-gray-500">{i + 1}</td>
                  <td className="px-4 py-3 text-white font-medium">{p.name}</td>
                  <td className="px-4 py-3 text-right text-white">{fmt(p.revenue)}</td>
                  <td className="px-4 py-3 text-right text-red-400">{fmt(p.cogs)}</td>
                  <td className="px-4 py-3 text-right text-green-400 font-medium">{fmt(p.contributionMargin)}</td>
                  <td className="px-4 py-3 text-right text-green-400">{fmtPct(p.contributionPct)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Margin eroders */}
      {data.marginEroders.length > 0 && (
        <div className="bg-gray-900 border border-red-900/50">
          <div className="px-4 py-3 border-b border-red-900/50">
            <h3 className="text-[11px] font-semibold tracking-widest uppercase text-red-400">Margin Eroders (Negative Contribution)</h3>
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-800 text-[11px] font-semibold tracking-widest uppercase text-gray-400">
                <th className="text-left px-4 py-3">Product</th>
                <th className="text-right px-4 py-3">Qty Sold</th>
                <th className="text-right px-4 py-3">Revenue</th>
                <th className="text-right px-4 py-3">COGS</th>
                <th className="text-right px-4 py-3">Loss</th>
              </tr>
            </thead>
            <tbody>
              {data.marginEroders.map(p => (
                <tr key={p.productId} className="border-b border-gray-800/50 bg-red-950/20">
                  <td className="px-4 py-3 text-red-400 font-medium">{p.name}</td>
                  <td className="px-4 py-3 text-right text-gray-400">{p.qty}</td>
                  <td className="px-4 py-3 text-right text-white">{fmt(p.revenue)}</td>
                  <td className="px-4 py-3 text-right text-red-400">{fmt(p.cogs)}</td>
                  <td className="px-4 py-3 text-right text-red-400 font-medium">{fmt(p.contributionMargin)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Full product table */}
      {data.products.length > 0 && (
        <div className="bg-gray-900 border border-gray-800 overflow-x-auto">
          <div className="px-4 py-3 border-b border-gray-800">
            <h3 className="text-[11px] font-semibold tracking-widest uppercase text-gray-400">
              All Products ({data.products.length})
            </h3>
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-800 text-[11px] font-semibold tracking-widest uppercase text-gray-400">
                <th className="text-left px-4 py-3">Product</th>
                <th className="text-left px-4 py-3">Code</th>
                <th className="text-right px-4 py-3">Qty</th>
                <th className="text-right px-4 py-3">Revenue</th>
                <th className="text-right px-4 py-3">COGS</th>
                <th className="text-right px-4 py-3">CM</th>
                <th className="text-right px-4 py-3">CM %</th>
              </tr>
            </thead>
            <tbody>
              {data.products.map(p => (
                <tr
                  key={p.productId}
                  className={`border-b border-gray-800/50 hover:bg-gray-800/30 ${p.contributionMargin < 0 ? 'bg-red-950/10' : ''}`}
                >
                  <td className={`px-4 py-3 font-medium ${p.contributionMargin < 0 ? 'text-red-400' : 'text-white'}`}>{p.name}</td>
                  <td className="px-4 py-3 text-gray-400 font-mono text-xs">{p.code}</td>
                  <td className="px-4 py-3 text-right text-gray-300">{p.qty}</td>
                  <td className="px-4 py-3 text-right text-white">{fmt(p.revenue)}</td>
                  <td className="px-4 py-3 text-right text-red-400">{fmt(p.cogs)}</td>
                  <td className={`px-4 py-3 text-right font-medium ${p.contributionMargin >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                    {fmt(p.contributionMargin)}
                  </td>
                  <td className={`px-4 py-3 text-right ${p.contributionPct >= 30 ? 'text-green-400' : p.contributionPct >= 0 ? 'text-amber-400' : 'text-red-400'}`}>
                    {fmtPct(p.contributionPct)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {data.products.length === 0 && (
        <div className="bg-gray-900 border border-gray-800 p-12 text-center text-gray-500">
          No sales data found for this period.
        </div>
      )}
    </div>
  )
}

// ─────────────────────────────────────────
// Main Intelligence Page
// ─────────────────────────────────────────

export default function Intelligence() {
  const { currentDepartmentId } = useDepartment()
  const [tab, setTab] = useState('forecast')

  return (
    <div className="min-h-screen bg-gray-950 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-8">
        {/* Header */}
        <div className="flex items-center gap-3 mb-8">
          <Brain size={28} className="text-brand-400" />
          <div>
            <h1 className="font-display text-3xl tracking-wide text-white">Business Intelligence</h1>
            <p className="text-sm text-gray-400">ML-powered demand forecasting, anomaly detection & pricing optimisation</p>
          </div>
        </div>

        {/* Tab bar */}
        <div className="flex gap-1 mb-8 overflow-x-auto border-b border-gray-800">
          {TABS.map(t => {
            const Icon = t.icon
            return (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className={`flex items-center gap-2 px-5 py-3 text-sm font-medium whitespace-nowrap transition-colors border-b-2 -mb-px ${
                  tab === t.id
                    ? 'text-brand-400 border-brand-500'
                    : 'text-gray-400 border-transparent hover:text-gray-200 hover:border-gray-600'
                }`}
              >
                <Icon size={16} />
                {t.label}
              </button>
            )
          })}
        </div>

        {/* Tab content */}
        {tab === 'forecast' && <DemandForecastTab departmentId={currentDepartmentId} />}
        {tab === 'anomalies' && <AnomalyDetectionTab departmentId={currentDepartmentId} />}
        {tab === 'pricing' && <PricingSignalsTab departmentId={currentDepartmentId} />}
        {tab === 'margin' && <ContributionMarginTab departmentId={currentDepartmentId} />}
      </div>
    </div>
  )
}
